import { type ISupportMessage } from '../interface/support-chat';
import { firstNameOf } from './chat-counterpart-copy';
import { type InboxUnitState } from './peer-inbox';

// Every sentence a parent ↔ child conversation shows that is not already in the shared chat
// copy table. Pure, so each one is pinned by a test and the panel stays a view.
//
// Register: plain verbs, sentence case, they/them, no apologies, and every failure line ends
// with what happens to the message.

export type PeerRole = 'parent' | 'child';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `09:09` — the 24-hour clock the plan specifies for chips and rows. */
export const clock = (date: Date) => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

/** `13 Jul` — day first, as the field writes dates. */
export const shortDate = (date: Date) => `${date.getDate()} ${MONTHS[date.getMonth()]}`;

export interface IPresenceReading {
	/** Last heartbeat, as the server reported it. Null when the tracker has no reading. */
	lastSeenAt: string | null;
	/** Whether the counterpart is in THIS conversation's hub group right now. */
	inThisThread: boolean;
}

/**
 * Presence as a sentence, never a dot.
 *
 * <p>The system knows when a phone last reported in and when a person last joined this thread.
 * It never knows that someone is offline, so that word does not appear; "No app contact yet"
 * says what was measured. "Checking…" exists because the admin header once asserted an absence
 * for about a second before the lookup settled, and operators believed it.</p>
 */
export const presenceLine = (reading: IPresenceReading | null, loading: boolean, now = Date.now()): string => {
	if (loading && !reading) return 'Checking…';
	if (!reading?.lastSeenAt) return 'No app contact yet';

	const seen = Date.parse(reading.lastSeenAt);
	if (!Number.isFinite(seen)) return 'No app contact yet';
	const age = now - seen;

	if (reading.inThisThread && age < MINUTE) return 'Reading this chat';
	if (age < MINUTE) return 'In the app just now';
	if (age < HOUR) return `In the app ${Math.max(1, Math.round(age / MINUTE))} min ago`;

	const date = new Date(seen);
	const today = new Date(now);
	if (sameDay(date, today)) return `In the app at ${clock(date)}`;

	const yesterday = new Date(now - 24 * HOUR);
	if (sameDay(date, yesterday)) return `In the app yesterday at ${clock(date)}`;

	return `Last in the app ${shortDate(date)}`;
};

/** The counterpart's unit, as the parent's list already labels it; `stalled` is the child's own pipeline. */
export type ConversationUnitState = InboxUnitState | 'stalled';

/**
 * Quick messages, by who is writing and what the unit is doing.
 *
 * <p>These are the intents a ward agent and a polling-unit agent actually exchange on election
 * day. A chip INSERTS its text into the composer and nothing more: the first message to a
 * person under pressure is read by a human before it goes.</p>
 */
export const quickMessages = (role: PeerRole, unitState: ConversationUnitState, unitCode?: string): string[] => {
	if (role === 'parent') {
		switch (unitState) {
			case 'conflict':
				return [
					`Is this the right sheet for ${unitCode ?? 'your unit'}?`,
					'Send a clearer photo of the sheet.',
					'Call me now.',
					'Passed to the support desk — hold on.',
				];
			case 'uploaded':
				return ['Got your sheet, thanks.', 'Stay at the unit until you’re released.', 'Call me when you can.'];
			default:
				return [
					'Are you at the unit?',
					'Upload the sheet as soon as it’s counted.',
					'Call me when you can.',
					'Send a photo of the sheet.',
				];
		}
	}

	switch (unitState) {
		case 'uploaded':
		case 'stalled':
			return ['Sheet uploaded.', 'Upload shows an error.', 'Problem at the unit — call me.', 'Can you raise this with the desk?'];
		default:
			return ['Sheet uploaded.', 'Need help with the upload.', 'Problem at the unit — call me.', 'Counting hasn’t started yet.'];
	}
};

export interface IEmptyStateInput {
	role: PeerRole;
	counterpartName: string;
	/** The counterpart's level, lower case: "ward", "LGA", "polling-unit". */
	counterpartLevel: string;
	unitCode?: string;
	unitState: ConversationUnitState;
	uploadedAt?: string | null;
}

/** The transcript's empty state. Leads with what the reader can do, not with the absence. */
export const emptyStateFor = ({
	role,
	counterpartName,
	counterpartLevel,
	unitCode,
	unitState,
	uploadedAt,
}: IEmptyStateInput): { title: string; body: string } => {
	const first = firstNameOf(counterpartName);

	if (role === 'child') {
		return {
			title: `Your ${counterpartLevel} agent is ${counterpartName}`,
			body: 'Ask for help with your upload, or tell them about a problem at the unit. For an emergency, call.',
		};
	}

	const code = unitCode ?? 'their unit';
	switch (unitState) {
		case 'uploaded': {
			const at = uploadedAt ? ` at ${clock(new Date(uploadedAt))}` : '';
			return { title: `Start with ${first}`, body: `${code} was uploaded${at}. Say thanks, or ask about the sheet.` };
		}
		case 'conflict':
			return { title: `Start with ${first}`, body: `${code} is marked Conflict. Ask ${first} to confirm the sheet.` };
		default:
			return {
				title: `Start with ${first}`,
				body: `${first} covers ${code} and hasn’t uploaded yet. Ask if they’re at the unit, or pick a quick message below.`,
			};
	}
};

/** Label for the pinned unit-state chip. */
export const unitStateChip = (
	unitState: ConversationUnitState,
	uploadedAt?: string | null
): { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' } => {
	switch (unitState) {
		case 'uploaded': {
			const date = uploadedAt ? new Date(uploadedAt) : null;
			const when = date && Number.isFinite(date.getTime()) ? ` ${shortDate(date)} ${clock(date)}` : '';
			return { label: `Uploaded${when}`, tone: 'success' };
		}
		case 'conflict':
			return { label: 'Conflict', tone: 'danger' };
		case 'stalled':
			return { label: 'Stalled — tap to view', tone: 'danger' };
		case 'pending':
			return { label: 'Pending', tone: 'warning' };
		default:
			return { label: 'Checking…', tone: 'neutral' };
	}
};

/**
 * The one muted line above the first message: what this thread is about, from metadata at
 * render time. State only — stamping the CURRENT state with the FIRST message's time would
 * read as "uploaded at 01:10" about a message sent hours before the upload.
 */
export const contextLine = (unitCode: string | undefined, unitState: ConversationUnitState): string | null => {
	if (!unitCode) return null;
	const state = unitState === 'unknown' ? null : unitStateChip(unitState).label.split(' ')[0];
	return state ? `About ${unitCode} · ${state}` : `About ${unitCode}`;
};

/** Max quoted length in an escalation, mirroring the server's reply context. */
const QUOTE_MAX = 140;

export interface IEscalationInput {
	role: PeerRole;
	unitCode: string;
	counterpartName: string;
	/** "polling-unit agent", "ward agent"… */
	counterpartRoleLabel: string;
	/** The counterpart's last message in the thread, if any. */
	lastFromCounterpart: Pick<ISupportMessage, 'body' | 'attachments' | 'createdAt' | 'kind'> | null;
}

/**
 * The draft seeded into the support composer by "Escalate to support desk".
 *
 * <p>Seeded once into an untouched composer and NEVER auto-sent. The quote is the
 * counterpart's last message so the desk gets the words, not the parent's summary of them.</p>
 */
export const escalationDraft = ({
	role,
	unitCode,
	counterpartName,
	counterpartRoleLabel,
	lastFromCounterpart,
}: IEscalationInput): string => {
	const first = firstNameOf(counterpartName);
	const roleLabel = counterpartRoleLabel.toLowerCase();
	const lead = role === 'parent' ? `Escalating ${unitCode} (${counterpartName}, ${roleLabel}).` : `Need the desk on ${unitCode}.`;
	// Who the quote is attributed to: the parent names the child by first name; the child
	// names their supervisor by role and full name, because the desk may not know the name.
	const who = role === 'parent' ? first : `My ${roleLabel} (${counterpartName})`;

	if (!lastFromCounterpart) return `${lead}\n\n`;

	const hasPhoto = (lastFromCounterpart.attachments?.length ?? 0) > 0 || lastFromCounterpart.kind === 'image';
	const raw = lastFromCounterpart.body.trim();
	const body = raw ? (raw.length > QUOTE_MAX ? `${raw.slice(0, QUOTE_MAX - 1)}…` : raw) : hasPhoto ? '(photo)' : '';
	if (!body) return `${lead}\n\n`;

	const at = clock(new Date(lastFromCounterpart.createdAt));
	return `${lead} ${who} at ${at}: "${body}"\n\n`;
};

/** When the roster says somebody else now covers the unit. */
export const reassignedNotice = (previousName: string, unitCode: string, newName: string | null): string =>
	newName
		? `${previousName} no longer covers ${unitCode}. ${newName} does now.`
		: `${previousName} no longer covers ${unitCode}. Nobody is assigned there yet.`;

export const INACTIVE_HINT = 'This account is marked inactive, so messages may not be seen. Call instead.';

/** What a counterpart is, for eyebrows and escalation text. */
export const roleLabelFor = (level: string | undefined, role: PeerRole): string => {
	const l = (level ?? '').toLowerCase().replace(/[\s_-]+/g, '');
	if (l.includes('polling')) return 'Polling-unit agent';
	if (l === 'ward') return 'Ward agent';
	if (l === 'lga') return 'LGA agent';
	if (l === 'state') return 'State agent';
	return role === 'parent' ? 'Supervising agent' : 'Agent';
};
